import { AnimationMixer, LoadingManager } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

export default class GLTFWrapper {
	constructor(
		assetUrl,
		id,
		callbackLoaded = null,
		callbackLoadError = null,
		loadingManager = new LoadingManager()
	) {
		this.ASSET_URL = assetUrl;
		this.ID = id;

		this.CALLBACK_LOADED = callbackLoaded;
		this.CALLBACK_LOAD_ERROR = callbackLoadError;

		this.GLTF = null;
		this.GLTF_SCENE = null;

		// Load
		const LOADER = new GLTFLoader(loadingManager);

		// LOADER.setCrossOrigin('');

		LOADER.load(
			assetUrl,
			this.onLoaded.bind(this),
			this.onLoadProgress.bind(this),
			this.onLoadError.bind(this)
		);

		// Init animation
		this.animationDuration = 0.0;
		this.animationCurrent = 0.0;

		// Init
		this.isDebug = false;
	}

	// ___________________________________________________________________ Loading

	onLoadProgress(xhr) {
		this.loadProgress = xhr.loaded / xhr.total;
	}

	onLoaded(gltf) {
		// Wrap with try catch to stop gltf laoder catching everything and hiding errors T_T
		try {
			// Store
			this.GLTF = gltf;
			this.GLTF_SCENE = gltf.scene;

			// Debug
			if (this.isDebug === true) {
				/* eslint-disable-next-line no-console */
				console.log(`GLTFWrapper. onLoaded: ${this.ASSET_URL}`);

				if (this.GLTF_SCENE !== null) {
					this.GLTF_SCENE.traverse(function traverseName(childIn) {
						const CHILD = childIn;

						if (CHILD.isMesh === true) {
							/* eslint-disable-next-line no-console */
							console.log(`- ${CHILD.name}`);
						}
					});
				}

				/* eslint-disable-next-line no-console */
				console.log('');
			}

			// Tidy
			this.LOADER = null;

			// Loaded
			if (this.CALLBACK_LOADED !== null) {
				this.CALLBACK_LOADED(this.ID);
			}
		} catch (e) {
			console.error(e);
		}
	}

	onLoadError(event) {
		if (this.isDebug === true) {
			/* eslint-disable-next-line no-console */
			console.error(`GLTFWrapper. onLoadError. ${this.ASSET_URL}`);
			console.log(event);
		}

		// Error
		if (this.CALLBACK_LOAD_ERROR !== null) {
			this.CALLBACK_LOAD_ERROR(this.ASSET_URL, event);
		}
	}

	// ______________________________________________________________ Add / Remove

	addTo(object3d) {
		object3d.add(this.GLTF_SCENE);
	}

	removeFrom(object3d) {
		object3d.remove(this.GLTF_SCENE);
	}

	// __________________________________________________________________ Material

	materialSet(name, material) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE !== null) {
			GLTF_SCENE.traverse(function traverseMaterial(childIn) {
				const CHILD = childIn;

				if (CHILD.isMesh === true) {
					if (CHILD.name === name) {
						CHILD.material = material;
					}
				}
			});
		}
	}

	materialSetAll(material) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE !== null) {
			GLTF_SCENE.traverse(function traverseMaterial(childIn) {
				const CHILD = childIn;

				if (CHILD.isMesh === true) {
					CHILD.material = material;
				}
			});
		}
	}

	// ___________________________________________________________________ Opacity

	opacitySet(opacity) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE !== null) {
			GLTF_SCENE.traverse(function traverseMaterial(childIn) {
				const CHILD = childIn;

				if (CHILD.isMesh === true) {
					CHILD.material.opacity = opacity;
				}
			});
		}
	}

	// ____________________________________________________________________ Shadow

	shadowsEnable() {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE !== null) {
			GLTF_SCENE.traverse(function traverseShadow(childIn) {
				const CHILD = childIn;

				if (CHILD.isMesh === true) {
					CHILD.castShadow = true;
					CHILD.receiveShadow = true;
				}
			});
		}
	}

	// ___________________________________________________________ Environment Map

	environmentMapSet(envMap) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE !== null) {
			GLTF_SCENE.traverse(function traverseMap(childIn) {
				const CHILD = childIn;

				if (CHILD.isMesh === true) {
					CHILD.envMap = envMap;
				}
			});
		}
	}

	environmentMapIntensitySet(envMapIntensity) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE !== null) {
			GLTF_SCENE.traverse(function traverseMapIntensity(childIn) {
				const CHILD = childIn;

				if (CHILD.isMesh === true) {
					CHILD.envMapIntensity = envMapIntensity;
				}
			});
		}
	}

	// _________________________________________________________________ Animation

	animationInitialise() {
		const { GLTF } = this;
		const { GLTF_SCENE } = this;

		// Create Animation Mixer
		this.ANIMATION_MIXER = new AnimationMixer(GLTF_SCENE);
		this.ANIMATION_CLIPS = GLTF.animations;

		// Optimise Clips
		this.ANIMATION_CLIPS.forEach(clip => {
			if (clip.validate()) {
				clip.optimize();
			}
		});

		// Create Actions
		this.ANIMATION_ACTIONS = [];

		this.ANIMATION_CLIPS.forEach(clip => {
			const ACTION = this.ANIMATION_MIXER.clipAction(clip);

			this.ANIMATION_ACTIONS.push(ACTION);

			// ACTION.halt();
		});

		// Animation duration from longest clip
		let duration = 0.0;

		this.ANIMATION_CLIPS.forEach(clip => {
			if (clip.duration > duration) {
				duration = clip.duration;
			}
		});

		this.animationDuration = duration;
	}

	animationSetTime(time) {
		const { ANIMATION_ACTIONS } = this;

		if (ANIMATION_ACTIONS) {
			// Set Action frame
			ANIMATION_ACTIONS.forEach(actionIn => {
				const ACTION = actionIn;

				// Set Animation position and avoid stutter
				ACTION.play();
				ACTION.time = time;
				ACTION.halt();
			});
		}

		this.animationCurrent = time;
	}

	animationUpdate(frameDurationSeconds) {
		const { ANIMATION_MIXER } = this;

		if (ANIMATION_MIXER) {
			ANIMATION_MIXER.update(frameDurationSeconds);
		}
	}

	animationGetDuration() {
		return this.animationDuration;
	}

	animationGetCurrent() {
		return this.animationCurrent;
	}

	animationSetCurrent(time) {
		this.animationCurrent = time;
	}

	// __________________________________________________________________ Position

	positionSet(x, y, z) {
		this.GLTF_SCENE.position.set(x, y, z);
	}

	positionSetX(x) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE != null) {
			this.GLTF_SCENE.position.x = x;
		}
	}

	positionSetY(y) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE != null) {
			this.GLTF_SCENE.position.y = y;
		}
	}

	positionSetZ(z) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE != null) {
			this.GLTF_SCENE.position.z = z;
		}
	}

	// _____________________________________________________________________ Scale

	scaleSet(scale) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE != null) {
			this.GLTF_SCENE.scale.x = scale;
			this.GLTF_SCENE.scale.y = scale;
			this.GLTF_SCENE.scale.z = scale;
		}
	}

	// _________________________________________________________________ Wireframe

	wireframeSet(wireframe) {
		const { GLTF_SCENE } = this;

		if (GLTF_SCENE !== null) {
			GLTF_SCENE.traverse(function traverseWireframe(childIn) {
				const CHILD = childIn;

				if (CHILD.isMesh === true) {
					CHILD.material.wireframe = wireframe; // = envMapIntensity;
				}
			});
		}
	}

	// _____________________________________________________________________ Debug

	debugSet(isDebug) {
		this.isDebug = isDebug;
	}

	// _____________________________________________________________________ Debug

	destroy() {
		// TODO Ensure memory is cleared
		this.GLTF = null;
		this.GLTF_SCENE = null;
	}

	// ____________________________________________________________________ Access

	getLoadProgress() {
		return this.loadProgress;
	}
}
